import React, { useContext } from 'react'
import cx from 'classnames'
import { Popover } from '@manychat/manyui'

import { EditorAiTextImproverMobileModal } from 'components/MCTextEditor/toolbar/popovers/EditorAiTextImprover/EditorAiTextImproverMobileModal'
import { EditorContext } from 'components/RichTextEditor/EditorContext'
import { useMediaQuery } from 'utils/commonHooks'

import { IToolHandler } from '../types'

import { ToolsToggles, ToolsPopovers } from './constants'
import { Tools } from './toolbarInterfaces'

import cm from './Toolbar.module.css'

export interface IToolItemProps {
  tool: Tools
  isSelected: boolean
  onSelect: (tool: string) => void
  onInsert: IToolHandler
  focusEditor?: () => void
  toolbarRef: React.RefObject<HTMLDivElement | null>
}

const ToolItem = ({
  tool,
  isSelected,
  onSelect,
  onInsert,
  focusEditor,
  toolbarRef,
}: IToolItemProps) => {
  const rootRef = React.useRef(null)

  const handlePopoverCloseReq = React.useCallback(() => {
    onSelect('')
    focusEditor?.()
  }, [focusEditor, onSelect])

  const handleClick = React.useCallback(() => {
    onSelect(tool)
  }, [onSelect, tool])

  const toggle = ToolsToggles[tool]

  const isMobile = useMediaQuery('(width <= 480px)')

  const editorInputRef = useContext(EditorContext).input

  if (!toggle) {
    return null
  }

  const toggleProps = {
    ref: rootRef,
    className: cx(isSelected ? cm.successText : 'bg-h-secondary', 'pointer withEmoji', {}),
    role: 'button',
    onClick: handleClick,
    'data-test-id': toggle.testId,
  }

  const ToolPopover = ToolsPopovers[tool]

  const isAiTool = tool === Tools.aiContentGenerator

  return (
    <>
      {React.isValidElement(toggle.component) ? (
        React.cloneElement(toggle.component, { ...toggleProps })
      ) : (
        <span data-onboarding-id={toggle.testId} {...toggleProps}>
          {React.isValidElement(toggle.icon) ? toggle.icon : toggle.label}
        </span>
      )}

      {isMobile && isAiTool && isSelected && (
        <EditorAiTextImproverMobileModal onSelect={onInsert} onClose={handlePopoverCloseReq} />
      )}

      {((!isMobile && isAiTool) || !isAiTool) && (
        <Popover
          className={cx(isAiTool ? cm.noBg : null)}
          open={isSelected}
          anchor={isAiTool ? editorInputRef ?? toolbarRef.current : rootRef.current}
          anchorOrigin={isAiTool ? 'right top' : undefined}
          popoverOrigin={isAiTool ? 'left top' : undefined}
          arrow={tool !== Tools.aiContentGenerator}
          at={isAiTool ? undefined : 'bottom'}
          onRequestClose={handlePopoverCloseReq}
          useLayerForClickAway={false}
          autoPosition="shift flip"
          preventBodyScroll
          theme={{
            base: isAiTool ? cm.aiPopover : null,
          }}
        >
          <ToolPopover onSelect={onInsert} />
        </Popover>
      )}
    </>
  )
}

export default ToolItem
